import { useLangStore, type Lang } from '@/stores/langStore';

/**
 * Bilingual (EN/HE) label catalog + enum mappers for the agent console.
 *
 * T9.1 (OP-165) — before this, every user-visible string was a hardcoded
 * English literal and raw backend enums (state/urgency/sender/channel) were
 * rendered verbatim. This module is the one place strings live; components
 * call `useT()` for chrome strings and the enum mappers for backend values.
 *
 * Rules:
 *  - DISPLAY ONLY. Never feed a label back to an API or use it as a key —
 *    wire values (enum strings, learning-tag values) are unchanged.
 *  - Tests key off `data-testid`, not visible text, so adding/changing a
 *    label here is safe for the suite.
 */

interface Entry {
  en: string;
  he: string;
}

/** Chrome strings (not backend enums). Seeded for T9.1; extended in T9.2–T9.6. */
export const LABELS = {
  // ── T9.1 (original) ─────────────────────────────────────────────────
  appTitle: { en: 'Agent Console', he: 'מסוף הנציג' },
  agentFallbackName: { en: 'Agent', he: 'נציג' },
  noActiveConversations: { en: 'No active conversations', he: 'אין שיחות פעילות' },
  noMessagesYet: { en: 'No messages yet', he: 'אין הודעות עדיין' },
  unreadSuffix: { en: 'new', he: 'חדש' },
  languageToggle: { en: 'עברית', he: 'English' },

  // ── T9.2 — LoginPage ─────────────────────────────────────────────────
  loginCompanyCode: { en: 'Company code', he: 'קוד ארגון' },
  loginUsername: { en: 'Username', he: 'שם משתמש' },
  loginPassword: { en: 'Password', he: 'סיסמה' },
  loginSignIn: { en: 'Sign In', he: 'התחבר' },
  loginSigningIn: { en: 'Signing in…', he: 'מתחבר…' },
  loginHandoffExpired: {
    en: 'That sign-in link expired. Please sign in again.',
    he: 'קישור הכניסה פג תוקף. נא להתחבר שוב.',
  },
  // Sign-in hand-off from the marketing site (HandoffPage)
  handoffSigningIn: { en: 'Signing you in…', he: 'מחבר אותך…' },

  // ── T9.2 — loginErrorMessage friendly strings ─────────────────────
  loginErrNetwork: {
    en: 'Cannot reach the server. Try again later.',
    he: 'לא ניתן להגיע לשרת. נסה שוב מאוחר יותר.',
  },
  loginErrCredentials: {
    en: 'Incorrect username or password. Please try again.',
    he: 'שם משתמש או סיסמה שגויים. נסה שוב.',
  },
  loginErrLocked: {
    en: 'Account locked or suspended. Contact your administrator.',
    he: 'החשבון נעול או מושהה. פנה למנהל המערכת.',
  },
  // 403 that is NOT a temporary-password account (that one goes to the change-password
  // screen) and NOT a lock (that is a 401 account_locked): we cannot name the reason, so
  // we do not claim "locked or suspended". English in both languages by operator decision.
  loginErrForbidden: {
    en: "You can't sign in with this account right now. Contact your administrator.",
    he: "You can't sign in with this account right now. Contact your administrator.",
  },
  // 401 account_locked (OP-224) — same wording as admin-portal.
  loginErrLockedMinutesOne: {
    en: 'This account is temporarily locked after too many failed attempts. Try again in about {n} minute, or ask an admin to unlock it.',
    he: 'החשבון נחסם זמנית לאחר יותר מדי ניסיונות כושלים. נסה שוב בעוד כ-{n} דקה, או פנה למנהל כדי לשחרר אותו.',
  },
  loginErrLockedMinutesMany: {
    en: 'This account is temporarily locked after too many failed attempts. Try again in about {n} minutes, or ask an admin to unlock it.',
    he: 'החשבון נחסם זמנית לאחר יותר מדי ניסיונות כושלים. נסה שוב בעוד כ-{n} דקות, או פנה למנהל כדי לשחרר אותו.',
  },
  loginErrLockedGeneric: {
    en: 'This account is temporarily locked after too many failed attempts. Try again later or ask an admin to unlock it.',
    he: 'החשבון נחסם זמנית לאחר יותר מדי ניסיונות כושלים. נסה שוב מאוחר יותר או פנה למנהל כדי לשחרר אותו.',
  },

  // ── Forced first-login password change (ChangePasswordPage) ──
  // Same wording as admin-portal's changePw* so both sites run one process. The rules
  // hint is English in both languages by operator decision (the server's own policy
  // message is English-only too).
  changePwMinLength: {
    en: 'New password must be at least {n} characters.',
    he: 'הסיסמה החדשה חייבת להכיל לפחות {n} תווים.',
  },
  changePwMismatch: {
    en: 'New password and confirmation do not match.',
    he: 'הסיסמה החדשה והאישור אינם תואמים.',
  },
  changePwSameAsTemp: {
    en: 'New password must be different from your temporary password.',
    he: 'הסיסמה החדשה חייבת להיות שונה מהסיסמה הזמנית.',
  },
  changePwTitle: { en: 'Set a new password', he: 'הגדרת סיסמה חדשה' },
  changePwSubtitle: {
    en: 'Your account uses a temporary password. Choose a new one to continue.',
    he: 'החשבון שלך משתמש בסיסמה זמנית. בחר סיסמה חדשה כדי להמשיך.',
  },
  changePwAccount: { en: 'Account', he: 'חשבון' },
  changePwNewPassword: { en: 'New password', he: 'סיסמה חדשה' },
  changePwRules: {
    en: 'At least 8 characters, with an uppercase letter, a number and a special character.',
    he: 'At least 8 characters, with an uppercase letter, a number and a special character.',
  },
  changePwConfirmPassword: { en: 'Confirm new password', he: 'אישור סיסמה חדשה' },
  changePwSaving: { en: 'Saving...', he: 'שומר...' },
  changePwSubmit: { en: 'Set new password', he: 'הגדר סיסמה חדשה' },
  changePwBackToLogin: { en: 'Back to login', he: 'חזרה להתחברות' },
  // Eye toggle on password fields (same wording as admin-portal). English in both languages by
  // operator decision, like the rules hint.
  passwordShow: { en: 'Show password', he: 'Show password' },
  passwordHide: { en: 'Hide password', he: 'Hide password' },
  loginErrServer: {
    en: 'Server error. Please try again in a moment.',
    he: 'שגיאת שרת. נסה שוב בעוד רגע.',
  },
  loginErrGeneric: { en: 'Login failed. Please try again.', he: 'ההתחברות נכשלה. נסה שוב.' },
  loginErrUnknownStatus: {
    en: 'Login failed (HTTP {status}). Please try again.',
    he: 'ההתחברות נכשלה (HTTP {status}). נסה שוב.',
  },

  // ── T9.2 — Sidebar ───────────────────────────────────────────────────
  sidebarWorkspace: { en: 'Workspace', he: 'סביבת עבודה' },
  sidebarConversations: { en: 'Conversations', he: 'שיחות' },
  sidebarAgentsOnline: { en: 'Agents Online: {n}', he: 'נציגים מחוברים: {n}' },
  sidebarLogout: { en: 'Logout', he: 'התנתק' },

  // ── T9.2 — NotFoundPage ──────────────────────────────────────────────
  notFoundTitle: { en: 'Page not found', he: 'הדף לא נמצא' },
  notFoundBack: { en: 'Back to Dashboard', he: 'חזרה ללוח' },

  // ── T9.2 — ConversationPanel ─────────────────────────────────────────
  selectConversation: {
    en: 'Select a conversation to view messages',
    he: 'בחר שיחה כדי לצפות בהודעות',
  },
  consultationTypeHint: {
    en: 'Type guidance below — the bot will relay it to the customer.',
    he: 'הקלד הנחיה למטה — הבוט יעביר אותה ללקוח.',
  },
  consultationProactiveHint: {
    en: 'Type guidance below to advise the bot at any time.',
    he: 'הקלד הנחיה למטה כדי לייעץ לבוט בכל עת.',
  },
  surfaceCustomerChat: { en: 'Customer Chat', he: 'צ׳אט לקוח' },
  surfaceBotConsultation: { en: 'Bot Consultation', he: 'ייעוץ לבוט' },
  guidanceNotDelivered: { en: 'GUIDANCE NOT DELIVERED', he: 'ההנחיה לא הועברה' },
  openConsultationDot: { en: 'open consultation', he: 'ייעוץ פתוח' },

  // ── T9.2 — CloseConversationButton ───────────────────────────────────
  closeConversation: { en: 'Close conversation', he: 'סגור שיחה' },
  closeConfirm: { en: 'Confirm close', he: 'אשר סגירה' },
  closeClosing: { en: 'Closing…', he: 'סוגר…' },
  closeCancel: { en: 'Cancel', he: 'בטל' },
  closeFailed: { en: 'Close failed — try again', he: 'הסגירה נכשלה — נסה שוב' },
  conv_menu_label: { en: 'More actions', he: 'פעולות נוספות' },
  conv_menu_move: { en: 'Move to status', he: 'העבר לסטטוס' },
  conv_menu_learning: { en: 'Learning', he: 'למידה' },
  conv_menu_learning_saved: {
    en: 'Saved. A learning article will appear in the admin portal under Learning, Articles.',
    he: 'נשמר. מאמר למידה יופיע בפורטל הניהול תחת למידה, מאמרים.',
  },
  conv_menu_learning_failed: { en: 'Could not save the learning choice.', he: 'לא ניתן לשמור את בחירת הלמידה.' },
  closeDialogBody: {
    en: 'The conversation moves to Closed and stays there. Choose whether the bot learns from it.',
    he: 'השיחה תעבור ל"סגורות" ותישאר שם. בחר אם הבוט ילמד ממנה.',
  },

  // ── T9.2 — TakeoverButton ────────────────────────────────────────────
  takeover: { en: 'Takeover', he: 'קח אחריות' },
  takingOver: { en: 'Taking over…', he: 'לוקח אחריות…' },

  // ── T9.2 — ReleaseButton ─────────────────────────────────────────────
  releaseToAi: { en: 'Release to AI', he: 'החזר לבוט' },
  releasing: { en: 'Releasing…', he: 'משחרר…' },

  // ── T9.2 — LetBotContinueButton ──────────────────────────────────────
  letBotContinue: { en: 'Let Bot Continue', he: 'תן לבוט להמשיך' },
  letBotContinueTitle: {
    en: 'Release this consultation. The bot will reply with its fallback message and resume handling.',
    he: 'שחרר את הייעוץ. הבוט ישלח הודעת ברירת מחדל וימשיך לטפל.',
  },
  letBotReleasing: { en: 'Releasing…', he: 'משחרר…' },

  // ── T9.2 — AgentReplyInput ───────────────────────────────────────────
  replyToCustomer: { en: 'REPLY TO CUSTOMER', he: 'תגובה ללקוח' },
  replyPlaceholder: {
    en: 'Type your reply — Enter to send, Shift+Enter for newline',
    he: 'הקלד תגובה — Enter לשליחה, Shift+Enter לשורה חדשה',
  },
  replySend: { en: 'Send', he: 'שלח' },
  replySending: { en: 'Sending…', he: 'שולח…' },

  // ── T9.4 — LearningTagControl de-jargon ──────────────────────────────
  learningHeader: { en: 'Teach the bot from this chat', he: 'למד את הבוט מהשיחה' },
  learningOptionDefault: { en: 'Normal', he: 'רגיל' },
  learningOptionSkip: { en: "Don't learn", he: 'אל תלמד' },
  learningOptionForceLearn: { en: 'Learn from this chat', he: 'למד מהשיחה' },
  learningOptionDefaultTitle: {
    en: 'A learning article is written when a teammate was consulted or took over, if learning articles are on.',
    he: 'מאמר למידה נכתב כשהתייעצו עם נציג או שנציג לקח שליטה, אם מאמרי הלמידה מופעלים.',
  },
  learningOptionSkipTitle: {
    en: 'Nothing is learned from this conversation.',
    he: 'לא לומדים כלום מהשיחה הזו.',
  },
  learningOptionForceLearnTitle: {
    en: 'Always learn from this session, even if the signal is weak.',
    he: 'למד תמיד מהשיחה הזו, גם אם האות חלש.',
  },
  learningNotePlaceholder: {
    en: 'Optional note for training…',
    he: 'הערה לאימון (לא חובה)…',
  },
  learningNoteAriaLabel: { en: 'Training note', he: 'הערת אימון' },
  learningTagAriaLabel: { en: 'Learning setting', he: 'הגדרת למידה' },
  learningSave: { en: 'Save', he: 'שמור' },
  learningSaving: { en: 'Saving…', he: 'שומר…' },
  learningFailedToSave: {
    en: 'Failed to save. Please try again.',
    he: 'השמירה נכשלה. נסה שוב.',
  },

  // ── T9.3 — Guest chat fallback label + anonymous/email-subject fallbacks ──
  guestChatLabel: { en: 'Guest chat #{code}', he: 'שיחה עם אורח #{code}' },
  noSubject: { en: '(no subject)', he: '(ללא נושא)' },
  anonymous: { en: 'Anonymous', he: 'אנונימי' },
  anonymousWithMessage: { en: 'Anonymous - {msg}', he: 'אנונימי - {msg}' },

  // ── T9.6 — SLA countdown ─────────────────────────────────────────────
  slaRemaining: { en: '{n}m left', he: 'נותרו {n} ד׳' },
  slaBreached: { en: 'Overdue by {n}m', he: 'באיחור של {n} ד׳' },

  // ── T9.5 — feel-good: action-failure + load states ───────────────────
  actionFailedRetry: { en: 'Action failed — try again', he: 'הפעולה נכשלה — נסה שוב' },
  conversationsLoading: { en: 'Loading conversations…', he: 'טוען שיחות…' },
  conversationsLoadError: {
    en: "Couldn't load conversations — they'll appear here as they arrive.",
    he: 'לא ניתן לטעון את השיחות — הן יופיעו כאן כשייכנסו.',
  },
  messagesLoadError: {
    en: "Couldn't load this conversation's history — try reopening it.",
    he: 'לא ניתן לטעון את היסטוריית השיחה — נסה לפתוח אותה מחדש.',
  },
  dismiss: { en: 'Dismiss', he: 'סגור' },

  // ── OP-327 — reconnect banner ─────────────────────────────────────────
  hubReconnectingBanner: {
    en: 'Connection lost — reconnecting…',
    he: 'החיבור אבד — מתחבר מחדש…',
  },
  // ── ADR-016 — load-based routing (read-only own-cap/load + routed cards) ──
  loadBadgeTooltip: {
    en: 'Your open conversations / your max concurrent conversations',
    he: 'השיחות הפתוחות שלך / מקסימום השיחות המקבילות שלך',
  },
  // ── Routed consultation badge (console v2, C2-6). A consultation the ADR-016 router targeted at a teammate who has
  // not answered is still TEAM SHARED (operator decision D3): the header says who it was routed to and still offers
  // Take over to everyone. Replaces 'Assigned to another agent', which said the opposite. he NEEDS NATIVE REVIEW.
  routedToBadge: { en: 'Routed to {name}', he: 'נותב אל {name}' },
  routedToBadgeUnknown: { en: 'Routed to a teammate', he: 'נותב לעמית לצוות' },
  routedToBadgeTitle: {
    en: 'The bot asked for help and it was routed to this teammate, but nobody has answered yet. Anyone on the team can take it.',
    he: 'הבוט ביקש עזרה והבקשה נותבה לעמית זה, אך איש עוד לא ענה. כל אחד בצוות יכול לקחת אותה.',
  },

  // ── ADR-017 D3 — conversation directory (collapsible per-category sections) ──
  uncategorizedSection: { en: 'No status', he: 'ללא סטטוס' },
  toggleSectionAria: { en: 'Toggle conversation status section', he: 'הרחב או כווץ סטטוס שיחה' },

  // ── WORKSTREAM D — CRM previous conversations (HubSpot interactions) ──
  crmHistoryTitle: { en: 'Previous conversations', he: 'שיחות קודמות' },
  crmHistoryLoading: { en: 'Loading history…', he: 'טוען היסטוריה…' },
  crmHistoryEmpty: { en: 'No past interactions', he: 'אין אינטראקציות קודמות' },
  crmHistoryUnavailable: { en: 'CRM history unavailable', he: 'היסטוריית CRM אינה זמינה' },

  // ═══════════════════════════════════════════════════════════════════
  // Agent Console redesign (v2) — ported from the BotableX design handoff
  // (ac-i18n.jsx). Keys use the design's snake_case names; interpolation
  // uses {var} tokens resolved by translate()/useT().t(key, vars).
  // ═══════════════════════════════════════════════════════════════════

  // ── chrome / nav ──
  poweredBy: { en: 'powered by BotableX', he: 'מבוסס על BotableX' },
  nav_myday: { en: 'My Day', he: 'היום שלי' },
  nav_conversations: { en: 'Conversations', he: 'שיחות' },
  nav_queue: { en: 'Live Queue', he: 'תור פעיל' },
  nav_team: { en: 'Team', he: 'צוות' },
  nav_settings: { en: 'Settings', he: 'הגדרות' },
  navSectionWorkspace: { en: 'Workspace', he: 'סביבת עבודה' },
  search_placeholder: { en: 'Search conversations, customers…', he: 'חיפוש שיחות, לקוחות…' },
  railExpand: { en: 'Expand menu', he: 'הרחב תפריט' },
  railCollapse: { en: 'Collapse menu', he: 'כווץ תפריט' },
  available: { en: 'Available', he: 'זמין' },
  busy: { en: 'Busy', he: 'עסוק' },
  away: { en: 'Away', he: 'מרוחק' },
  offlineStatus: { en: 'Offline', he: 'לא מחובר' },
  liveConnected: { en: 'Live', he: 'מחובר' },
  role_agent: { en: 'Support Agent', he: 'נציג תמיכה' },
  notificationsAria: { en: 'Notifications', he: 'התראות' },
  toggleThemeAria: { en: 'Toggle theme', he: 'החלף ערכת נושא' },

  // ── My Day ──
  greeting_morning: { en: 'Good morning, {name}', he: 'בוקר טוב, {name}' },
  greeting_day: { en: 'Hello, {name}', he: 'שלום, {name}' },
  greeting_sub: {
    en: "Here's what's happening on your desk right now.",
    he: 'הנה מה שקורה אצלך עכשיו.',
  },
  stat_assigned: { en: 'Assigned to me', he: 'משויך אליי' },
  stat_resolved: { en: 'Resolved today', he: 'נפתרו היום' },
  // HE NEEDS NATIVE REVIEW (new in C2-6): the resolved-today figure is read from the closed-conversations folder, a page at a time.
  stat_resolved_hint: {
    en: 'Approximate: counts the closed conversations loaded so far',
    he: 'משוער: סופר את השיחות הסגורות שנטענו עד כה',
  },
  stat_response: { en: 'Avg. first response', he: 'זמן תגובה ממוצע' },
  stat_csat: { en: 'CSAT this week', he: 'שביעות רצון השבוע' },
  sla_atrisk: { en: 'SLA at risk', he: 'SLA בסיכון' },
  sla_atrisk_sub: {
    en: '{n} conversations approaching their deadline',
    he: '{n} שיחות מתקרבות למועד היעד',
  },
  my_queue: { en: 'My open conversations', he: 'השיחות הפתוחות שלי' },
  activity: { en: 'Live activity', he: 'פעילות חיה' },
  activity_empty: {
    en: 'No recent activity yet — it will appear here as things happen.',
    he: 'אין עדיין פעילות — היא תופיע כאן ככל שדברים יקרו.',
  },
  view_all: { en: 'View all', he: 'הצג הכל' },
  due_in: { en: 'due in {time}', he: 'בעוד {time}' },
  overdue: { en: 'overdue {time}', he: 'באיחור {time}' },
  not_available: { en: 'Not available', he: 'לא זמין' },
  na_short: { en: 'N/A', he: 'לא זמין' },

  // ── activity feed lines (derived from live hub events) ──
  act_consult: { en: 'Bot requested help on {name}', he: 'הבוט ביקש עזרה אצל {name}' },
  act_resolved: { en: 'Conversation with {name} was resolved', he: 'השיחה עם {name} נפתרה' },
  act_new: { en: 'New conversation from {name} entered', he: 'שיחה חדשה מ-{name} נכנסה' },
  act_takeover: { en: 'You took over {name}', he: 'לקחת שליטה על {name}' },
  act_sla: { en: 'SLA warning on {name}', he: 'אזהרת SLA על {name}' },

  // ── lifecycle state badges (design st_*) ──
  st_new: { en: 'New', he: 'חדש' },
  st_ai: { en: 'AI handling', he: 'הבוט מטפל' },
  st_consult: { en: 'Bot needs help', he: 'הבוט מבקש עזרה' },
  st_takeover: { en: 'You took over', he: 'בטיפולך' },
  st_closed: { en: 'Closed', he: 'נסגר' },

  // ── inbox / directories ──
  inbox: { en: 'Inbox', he: 'תיבה' },
  inbox_sub: { en: '{n} conversations across {d} folders', he: '{n} שיחות ב-{d} תיקיות' },
  inbox_sub_one: { en: '1 conversation across {d} folders', he: 'שיחה אחת ב-{d} תיקיות' },
  dir_empty: { en: 'Nothing here right now', he: 'אין כאן שיחות כרגע' },
  unidentified: { en: 'Unidentified client', he: 'לקוח לא מזוהה' },

  // ── channels (design ch_*) ──
  ch_web: { en: 'Web chat', he: 'צ׳אט באתר' },
  ch_webchat: { en: 'Web chat', he: 'צ׳אט באתר' },
  ch_email: { en: 'Email', he: 'אימייל' },
  ch_whatsapp: { en: 'WhatsApp', he: 'וואטסאפ' },
  ch_messenger: { en: 'Messenger', he: 'מסנג׳ר' },
  ch_sms: { en: 'SMS', he: 'SMS' },

  // ── workspace / thread ──
  tab_customer: { en: 'Customer chat', he: 'צ׳אט עם הלקוח' },
  tab_consult: { en: 'Bot consultation', he: 'התייעצות עם הבוט' },
  btn_takeover: { en: 'Take over', he: 'קח שליטה' },
  btn_release: { en: 'Release to bot', he: 'החזר לבוט' },
  btn_letbot: { en: 'Let bot continue', he: 'תן לבוט להמשיך' },
  composer_customer: { en: 'Message {name}…', he: 'הודעה אל {name}…' },
  composer_guidance: {
    en: 'Advise the bot — it will relay to the customer…',
    he: 'ייעץ לבוט — הוא יעביר ללקוח…',
  },
  guidance_hint: {
    en: 'Guidance is relayed to the customer after the bot composes a reply.',
    he: 'ההנחיה תועבר ללקוח לאחר שהבוט מנסח תשובה.',
  },
  consult_empty: {
    en: 'The bot is asking for help on this conversation. Advise it below.',
    he: 'הבוט מבקש עזרה בשיחה הזו. ייעץ לו למטה.',
  },
  select_convo: { en: 'Select a conversation', he: 'בחר שיחה' },
  select_convo_sub: {
    en: 'Pick a conversation from the inbox to view the thread and customer context.',
    he: 'בחר שיחה מהתיבה כדי לראות את ההתכתבות ופרטי הלקוח.',
  },
  sender_bot: { en: 'Bot', he: 'בוט' },
  sender_you: { en: 'You', he: 'את/ה' },
  // OP-1041 - a human teammate's message after a reload. Used when the sender is an agent who is neither
  // the signed-in user ("You") nor in the agent directory (deleted, or the directory fetch failed):
  // neutral on purpose - it must never read "You" for somebody else, and never show a raw id.
  sender_agent_unknown: { en: 'An agent', he: 'נציג' },

  // ── AnswerBar (console v2, C1-4): ONE bar, its target decided by state and owner, never by the filter ──
  // he copied from the approved prototype's i18n.ts where the concept exists (ab_to, ab_to_bot_internal, ab_send_*,
  // ab_takeover_hint, ab_enter_hint). HE NEEDS NATIVE REVIEW: ab_closed (the prototype's text offered a Reopen the
  // real console does not have), ab_owned_by_other, ab_handled_elsewhere, ab_readonly_view, ab_refused, ab_routed_to,
  // ab_routed_unknown, ab_confirming_owner, ab_owner_unconfirmed, ab_no_handle (the prototype has no
  // routed-consultation, owner-confirmation or no-permission line, so all five are new Hebrew).
  ab_to: { en: 'To', he: 'אל' },
  ab_to_bot_internal: {
    en: 'Bot — internal consultation. The customer will not see this.',
    he: 'הבוט — התייעצות פנימית. הלקוח לא יראה את זה.',
  },
  ab_takeover_hint: { en: 'Want to write to the customer yourself?', he: 'רוצים לכתוב ללקוח בעצמכם?' },
  ab_send_customer: { en: 'Send to customer', he: 'שליחה ללקוח' },
  ab_send_bot: { en: 'Send to bot', he: 'שליחה לבוט' },
  ab_enter_hint: { en: 'Enter to send · Shift+Enter for a new line', he: 'Enter לשליחה · Shift+Enter לשורה חדשה' },
  ab_closed: {
    en: 'This conversation is closed. A returning customer starts a new one.',
    he: 'השיחה סגורה. לקוח שחוזר פותח שיחה חדשה.',
  },
  ab_owned_by_other: {
    en: '{name} is handling this conversation. Only they can write here.',
    he: '{name} מטפל/ת בשיחה הזו. רק הם כותבים כאן.',
  },
  ab_handled_elsewhere: {
    en: 'Another agent is handling this conversation. Only they can write here.',
    he: 'נציג/ה אחר/ת מטפל/ת בשיחה הזו. רק הם כותבים כאן.',
  },
  ab_readonly_view: { en: 'Read-only view — Senior agent / Admin', he: 'תצוגה לקריאה בלבד — נציג בכיר / מנהל' },
  ab_refused: {
    en: 'Nothing was sent: this conversation is no longer yours to answer (it may have just been taken).',
    he: 'דבר לא נשלח: השיחה הזו כבר אינה בטיפולך (ייתכן שנלקחה זה עתה).',
  },
  // A consultation the router sent to a teammate who has not answered yet. Informational: it is still team shared
  // (operator decision D3), so the guidance composer stays.
  ab_routed_to: {
    en: 'Routed to {name} — still team shared. You can add guidance for the bot.',
    he: 'הופנתה אל {name} — עדיין משותפת לצוות. אפשר להוסיף הנחיה לבוט.',
  },
  // Shown while a fallback owner is not yet confirmed by the server: neutral, never "another agent".
  ab_confirming_owner: {
    en: 'Confirming who owns this conversation…',
    he: 'מאמתים מי מטפל בשיחה…',
  },
  // Shown once the owner re-read ENDED without an answer (a failure, a 404): nothing is being confirmed any more, so the
  // bar must not say "Confirming", and it must not say "another agent" either (the owner may be me). Neutral, and it says
  // how to check again: reopening the conversation starts a fresh check. HE NEEDS NATIVE REVIEW (new Hebrew).
  ab_owner_unconfirmed: {
    en: 'Could not confirm who owns this conversation. Reopen it to check again.',
    he: 'לא ניתן היה לאמת מי מטפל בשיחה הזו. פתחו אותה מחדש כדי לבדוק שוב.',
  },
  // A viewer who holds no workspace.handle can read the conversation but not act on it: no composer, no Take over.
  // HE NEEDS NATIVE REVIEW (new Hebrew).
  ab_no_handle: {
    en: 'You can read this conversation, but you do not have permission to reply in it.',
    he: 'אפשר לקרוא את השיחה הזו, אך אין לך הרשאה להשיב בה.',
  },
  ab_routed_unknown: {
    en: 'Routed to a teammate — still team shared. You can add guidance for the bot.',
    he: 'הופנתה לחבר/ת צוות — עדיין משותפת לצוות. אפשר להוסיף הנחיה לבוט.',
  },

  // ── Merged chat timeline (console v2, C1-3): ONE timeline with a segmented filter, an avatar per message ──
  // he copied from the approved prototype's i18n.ts where the concept exists; tl_empty_filter and
  // tl_filter_aria are new Hebrew and NEED NATIVE REVIEW before release. tl_consult_internal, tl_agent_to_bot and
  // tl_agent_to_customer are ASSEMBLED from prototype pieces (its consultation / bot / customer words joined with the
  // arrow), not copied verbatim - they NEED NATIVE REVIEW too.
  tl_filter_all: { en: 'All', he: 'הכל' },
  tl_filter_chat: { en: 'Customer chat', he: 'שיחת לקוח' },
  tl_filter_consult: { en: 'Consultation', he: 'התייעצות' },
  tl_filter_aria: { en: 'Timeline filter', he: 'סינון ציר הזמן' },
  tl_consult_internal: { en: 'Consultation · internal', he: 'התייעצות · פנימי' },
  tl_bot_to_agents: { en: 'Bot → agents', he: 'בוט ← נציגים' },
  tl_agent_to_bot: { en: '{name} → bot', he: '{name} ← לבוט' },
  tl_agent_to_customer: { en: '{name} → customer', he: '{name} ← ללקוח' },
  tl_empty_filter: { en: 'Nothing in this view yet.', he: 'אין עדיין פריטים בתצוגה הזו.' },
  tl_load_error: { en: 'The conversation could not be loaded, so this view may be incomplete.', he: 'לא ניתן היה לטעון את השיחה, ולכן ייתכן שהתצוגה חלקית.' },
  tl_retry: { en: 'Try again', he: 'נסה שוב' },
  tl_loading: { en: 'Loading the conversation...', he: 'טוען את השיחה...' },
  tl_load_earlier: { en: 'Load earlier messages', he: 'טען הודעות קודמות' },
  tl_load_earlier_n: { en: '{n} earlier messages not shown', he: '{n} הודעות קודמות אינן מוצגות' },

  // ── Chat action timeline (staff-only lines between the messages) ──────────────────────────────
  // Built from entry type + params, never from the server's English `summary`. Hebrew sentences are
  // phrased around the conversation or the noun so no verb has to agree with an actor whose gender is
  // unknown ("השיחה נלקחה על ידי {name}", not a guessed "לקח/ה").
  tl_you: { en: 'you', he: 'את/ה' },
  tl_you_suffix: { en: '(you)', he: '(את/ה)' },
  tl_someone: { en: 'a teammate', he: 'חבר צוות' },
  tl_consult_requested: { en: 'Bot asked for help', he: 'הבוט ביקש עזרה' },
  tl_consulted_agent: { en: 'Bot consulted {name}', he: 'הבוט התייעץ עם {name}' },
  tl_consulted_team: { en: 'Bot consulted the team', he: 'הבוט התייעץ עם הצוות' },
  tl_guidance_sent: { en: '{name} answered the bot', he: 'תשובה לבוט מאת {name}' },
  tl_takeover: { en: '{name} took over', he: 'השיחה נלקחה על ידי {name}' },
  tl_takeover_reason: { en: '{name} took over — {reason}', he: 'השיחה נלקחה על ידי {name} — {reason}' },
  tl_release: { en: '{name} handed back to the bot', he: 'השיחה הוחזרה לבוט על ידי {name}' },
  tl_dismissed: { en: '{name} let the bot continue', he: 'הבוט הורשה להמשיך על ידי {name}' },
  tl_expired: { en: 'No teammate replied', he: 'אף חבר צוות לא הגיב' },
  tl_closed_customer: { en: 'The customer said it is solved; consultation closed', he: 'הלקוח אמר שזה נפתר; ההתייעצות נסגרה' },
  tl_sla: { en: 'No teammate responded in time', he: 'אף חבר צוות לא הגיב בזמן' },
  tl_sla_n: {
    en: 'No teammate responded within the service window ({n} min)',
    he: 'אף חבר צוות לא הגיב בזמן ({n} דק׳)',
  },
  tl_closed_by: { en: 'Closed by {name}', he: 'השיחה נסגרה על ידי {name}' },
  tl_closed_by_customer: { en: 'Closed by the customer', he: 'השיחה נסגרה על ידי הלקוח' },
  tl_closed_inactivity: { en: 'Closed automatically after inactivity', he: 'השיחה נסגרה אוטומטית עקב חוסר פעילות' },
  tl_closed_system: { en: 'Closed by the system', he: 'השיחה נסגרה על ידי המערכת' },
  tl_closed: { en: 'Conversation closed', he: 'השיחה נסגרה' },
  tl_spam: { en: 'Marked as spam by {name}', he: 'סומנה כספאם על ידי {name}' },
  tl_spam_unnamed: { en: 'Marked as spam', he: 'סומנה כספאם' },
  tl_spam_undone: { en: 'Spam mark undone by {name}', he: 'סימון הספאם בוטל על ידי {name}' },
  tl_spam_undone_unnamed: { en: 'Spam mark undone', he: 'סימון הספאם בוטל' },
  tl_draft_approved: {
    en: "{name} approved the bot's email reply",
    he: 'תשובת הבוט באימייל אושרה על ידי {name}',
  },
  tl_draft_edited: {
    en: "{name} edited and sent the bot's email reply",
    he: 'תשובת הבוט באימייל נערכה ונשלחה על ידי {name}',
  },
  tl_draft_auto_maxwait: {
    en: "The bot's email reply was sent automatically (review time ran out)",
    he: 'תשובת הבוט באימייל נשלחה אוטומטית (זמן הבדיקה הסתיים)',
  },
  tl_draft_auto_idle: {
    en: "The bot's email reply was sent automatically (nobody reviewed it)",
    he: 'תשובת הבוט באימייל נשלחה אוטומטית (איש לא בדק אותה)',
  },
  tl_draft_resolved: { en: 'Email draft resolved', he: 'טיוטת האימייל טופלה' },
  tl_unknown: { en: 'Conversation event', he: 'פעולה בשיחה' },
  // OP-681 - shown on an agent's own bubble when the reply definitely did not reach the customer.
  // Says only that, deliberately: the reason code stays in the audit log and the operator tooling.
  msg_not_delivered: { en: 'Not delivered', he: 'לא נשלח' },
  // 2026-11-01 - the page the customer was on when they sent a message (a client claim, shown as plain text).
  msg_customer_page: { en: 'Customer was on: {page}', he: 'הלקוח היה בעמוד: {page}' },
  now: { en: 'now', he: 'עכשיו' },
  botTyping: { en: 'Bot is typing…', he: 'הבוט מקליד…' },
  readOnlyNotice: { en: 'View only', he: 'צפייה בלבד' },

  // ── ADR-019 email draft review ──
  draft_title: { en: 'Email reply — awaiting your review', he: 'תשובת אימייל — ממתינה לאישורך' },
  draft_marker_hint: {
    en: 'A [[media:N]] marker puts picture N beside that line in the email. Keep it to show the picture there, or delete it to attach the picture instead.',
    he: 'סימון [[media:N]] מציב את תמונה N לצד השורה באימייל. השאר אותו כדי להציג את התמונה שם, או מחק אותו כדי שהתמונה תצורף.',
  },
  draft_review_hint: {
    en: 'The bot drafted this reply. Approve it, ask for a revision, or write it yourself.',
    he: 'הבוט ניסח את התשובה הזו. אשר אותה, בקש תיקון, או כתוב אותה בעצמך.',
  },
  draft_revision: { en: 'Revision {n}', he: 'גרסה {n}' },
  draft_btn_approve: { en: 'Approve & send', he: 'אשר ושלח' },
  draft_btn_revise: { en: 'Ask for a revision', he: 'בקש תיקון' },
  draft_btn_edit: { en: 'Edit myself', he: 'ערוך בעצמי' },
  draft_revise_placeholder: { en: 'Tell the bot what to change…', he: 'הסבר לבוט מה לשנות…' },
  draft_edit_placeholder: {
    en: 'Edit the reply the customer will receive…',
    he: 'ערוך את התשובה שתישלח ללקוח…',
  },
  draft_revise_send: { en: 'Send instruction', he: 'שלח הנחיה' },
  draft_edit_send: { en: 'Send my reply', he: 'שלח את תשובתי' },
  draft_readonly_hint: { en: 'Another agent is reviewing this draft.', he: 'נציג אחר בודק את הטיוטה הזו.' },
  draft_autosend_in: { en: 'Sends automatically in {time}', he: 'תישלח אוטומטית בעוד {time}' },
  draft_autosend_due: { en: 'Sending now…', he: 'נשלחת עכשיו…' },
  // OP-582 — what a HELD draft says instead of the two lines above. Both of those were false
  // for a draft no clock can release: before the deadline it promised an automatic send that
  // was never coming, and after it (which always arrives, since maxWaitAtUtc is snapshotted on
  // held drafts too) it claimed "Sending now…" forever. The replacement states the two facts an
  // agent must act on: nothing will send this, and the customer is waiting on a person.
  draft_hold_no_autosend: {
    en: 'Waiting for a person — this reply will not send on its own.',
    he: 'ממתינה לאדם — התשובה הזו לא תישלח מעצמה.',
  },
  /** Age of the draft — the number that says "this is being neglected". */
  draft_waiting_for: { en: 'Waiting {time}', he: 'ממתינה {time}' },
  draft_revise_failed: {
    en: 'The revision failed ({reason}). The previous version is shown.',
    he: 'התיקון נכשל ({reason}). מוצגת הגרסה הקודמת.',
  },
  draft_rejected_not_owner: {
    en: 'Another agent already owns this draft — your action was not applied.',
    he: 'נציג אחר כבר מטפל בטיוטה הזו — הפעולה שלך לא בוצעה.',
  },

  // OP-582 — inbox / row / rail discovery of a waiting draft. Until these existed a held
  // draft was reachable ONLY by opening its conversation, i.e. by already knowing.
  draft_badge_waiting: { en: 'Reply waiting {time}', he: 'תשובה ממתינה {time}' },
  draft_badge_held: { en: 'Needs you {time}', he: 'דרוש אישורך {time}' },
  draft_queue_title: { en: 'Awaiting your review', he: 'ממתינות לאישורך' },
  draft_queue_hint: {
    en: 'The customer has not been answered until one of these is sent.',
    he: 'הלקוח לא קיבל תשובה עד שאחת מאלה תישלח.',
  },

  // ── customer context ──
  ctx_title: { en: 'Customer', he: 'לקוח' },
  ctx_since: { en: 'Customer since {date}', he: 'לקוח מאז {date}' },
  ctx_recent_orders: { en: 'Recent orders', he: 'הזמנות אחרונות' },
  ctx_past_convos: { en: 'Tickets & conversations', he: 'פניות ושיחות קודמות' },
  ctx_tags: { en: 'Tags', he: 'תגיות' },
  ctx_phone: { en: 'Phone', he: 'טלפון' },
  ctx_loading: { en: 'Loading customer…', he: 'טוען לקוח…' },
  ctx_none: { en: 'No CRM profile for this customer.', he: 'אין פרופיל CRM ללקוח זה.' },
  // ── CRM category picker (CRM v2 WS-6.1) ───────────────────────────────────────────────────────
  // "CRM category", not just "Category": the console already has a category concept driving the
  // lifecycle column, and these are deliberately independent axes. Naming this one after the system
  // it writes to is what stops an agent reading them as the same field.
  ctx_crm_category_title: { en: 'CRM category', he: 'קטגוריה ב-CRM' },
  ctx_crm_category_no_ticket: {
    en: 'No CRM ticket for this conversation yet.',
    he: 'עדיין אין קריאה ב-CRM עבור שיחה זו.',
  },
  ctx_crm_category_save_failed: {
    en: 'The CRM did not accept that change. Nothing was saved.',
    he: 'ה-CRM לא קיבל את השינוי. שום דבר לא נשמר.',
  },
  ctx_collapse: { en: 'Collapse panel', he: 'כווץ פאנל' },
  // Wording follows the panel: the collapsed rail now expands to CONVERSATION details, not customer
  // details. The key name is unchanged — its only consumer is that rail, and tests key off testids.
  ctx_expand: { en: 'Show conversation details', he: 'הצג פרטי שיחה' },
  ctx_anon_title: { en: 'Anonymous visitor', he: 'מבקר אנונימי' },
  ctx_anon_note: {
    en: 'This visitor is not identified — there is no CRM record to show.',
    he: 'המבקר אינו מזוהה — אין רשומת CRM להצגה.',
  },
  ctx_history_empty: {
    en: 'No past tickets or conversations yet',
    he: 'אין עדיין פניות או שיחות קודמות',
  },
  ctx_row_ticket: { en: 'Ticket', he: 'פנייה' },
  ctx_row_conversation: { en: 'Conversation', he: 'שיחה' },
  ctx_row_conversation_fallback: { en: 'Conversation', he: 'שיחה' },
  // ── Resolved company (CRM v2 WS-6.2) ──────────────────────────────────────────────────────────
  // "Company details" rather than "Company fields": an agent reads this as information about the
  // customer's organisation, not as a set of CRM property rows.
  ctx_company_details: { en: 'Company details', he: 'פרטי החברה' },
  // Shown next to the company name when the company came from an actual Company RECORD rather than
  // the free-text string on the contact. The distinction matters when an agent is deciding whether
  // to trust it: the free-text value is whatever someone once typed.
  ctx_company_from_record: { en: 'From CRM company record', he: 'מרשומת חברה ב-CRM' },
  sentiment_pos: { en: 'Positive', he: 'חיובי' },
  sentiment_neu: { en: 'Neutral', he: 'ניטרלי' },
  sentiment_neg: { en: 'Frustrated', he: 'מתוסכל' },

  // ── conversation panel / contact page ─────────────────────────────────────────────────────────
  // The right-hand panel is about the CONVERSATION; the contact keeps `ctx_title` ("Customer"),
  // which is now the contact page's own heading.
  conv_ctx_title: { en: 'Conversation', he: 'שיחה' },
  conv_subject: { en: 'Subject', he: 'נושא' },
  conv_subject_placeholder: { en: 'Name this conversation…', he: 'תן/י שם לשיחה…' },
  conv_subject_edit: { en: 'Edit subject', he: 'עריכת נושא' },
  conv_subject_save: { en: 'Save', he: 'שמירה' },
  conv_subject_cancel: { en: 'Cancel', he: 'ביטול' },
  // States plainly that nothing changed — a silent revert reads as "it didn't register" and the
  // agent retypes the same name forever.
  conv_subject_save_failed: {
    en: "The subject wasn't saved. Nothing was changed.",
    he: 'הנושא לא נשמר. שום דבר לא שונה.',
  },
  conv_owner: { en: 'Owner', he: 'אחראי' },
  conv_owner_unassigned: { en: 'Unassigned', he: 'לא משויך' },
  // The lifecycle badge (bot / consultation / takeover / closed). Named "System state" so it does not collide with the
  // tenant's own Conversation Status (conv_state_category) - PR-A rename, 2026-10-26.
  conv_status: { en: 'System state', he: 'מצב מערכת' },
  conv_channel: { en: 'Channel', he: 'ערוץ' },
  // The Botablex conversation-state axis (ADR-017), distinct from the tenant's CRM category shown
  // just below it by CrmCategoryPicker. User-visible name: "Conversation Status" (PR-A rename, 2026-10-26).
  conv_state_category: { en: 'Conversation Status', he: 'סטטוס שיחה' },
  // Same shape as conv_subject_save_failed: say the change did NOT take. The picker rolls the value
  // back on failure, and a silent revert reads as a UI glitch rather than a rejected write.
  conv_category_save_failed: {
    en: "The conversation status wasn't changed.",
    he: 'סטטוס השיחה לא שונה.',
  },
  // Surfaced as the control's tooltip. Setting the state by hand PINS it, so the server stops
  // re-categorising this conversation on later turns — a sticky decision the agent should be told
  // about before making it, not after.
  conv_category_pin_hint: {
    en: "Setting this by hand stops the bot changing this conversation's status automatically.",
    he: 'שינוי ידני עוצר את שינוי סטטוס השיחה האוטומטי על ידי הבוט.',
  },
  conv_created: { en: 'Opened', he: 'נפתחה' },
  conv_closed: { en: 'Closed at', he: 'נסגרה ב' },
  conv_last_message: { en: 'Last message', he: 'הודעה אחרונה' },
  conv_open_failed: {
    en: "That conversation couldn't be opened — it may have been removed.",
    he: 'לא ניתן לפתוח את השיחה — ייתכן שהוסרה.',
  },
  ctx_conv_props_section: { en: 'CRM properties', he: 'מאפייני CRM' },
  // Distinct from the read-failed and no-ticket cases below: this one is a normal early state.
  ctx_conv_props_no_ticket: {
    en: 'No CRM ticket for this conversation yet.',
    he: 'אין עדיין כרטיס CRM לשיחה הזו.',
  },
  // The values are UNKNOWN here, not absent — worth saying, because an empty card would read as "nothing set".
  ctx_conv_props_read_failed: {
    en: "Your CRM couldn't be read, so these values are unknown.",
    he: 'לא ניתן לקרוא מה-CRM, ולכן הערכים אינם ידועים.',
  },
  ctx_conv_props_unset: { en: '— none —', he: '— ללא —' },
  // Says the change did NOT take. The control rolls back on failure, and a silent revert reads as a glitch.
  ctx_conv_props_save_failed: {
    en: "The change wasn't saved to your CRM.",
    he: 'השינוי לא נשמר ב-CRM.',
  },
  ctx_contact_section: { en: 'Contact', he: 'איש קשר' },
  ctx_view_contact: { en: 'View contact', he: 'הצג איש קשר' },
  cp_trace_title: { en: 'For the Logs page', he: 'לחיפוש ביומן' },
  cp_trace_session: { en: 'Session ID', he: 'מזהה שיחה' },
  cp_trace_transaction: { en: 'Last transaction ID', he: 'מזהה טרנזקציה אחרון' },
  cp_trace_copy: { en: 'Copy', he: 'העתק' },
  cp_trace_copied: { en: 'Copied', he: 'הועתק' },
  cp_trace_none: { en: 'Not loaded yet', he: 'עדיין לא נטען' },
  contact_back: { en: 'Back', he: 'חזרה' },
  contact_load_more: { en: 'Load more', he: 'טען עוד' },
  contact_open_conversation: { en: 'Open this conversation', he: 'פתח שיחה זו' },
  contact_conversations_title: { en: 'Tickets & conversations', he: 'פניות ושיחות' },

  // ── queue ──
  queue_title: { en: 'Live queue', he: 'תור פעיל' },
  queue_sub: { en: '{n} conversations waiting to be claimed', he: '{n} שיחות ממתינות לשיוך' },
  queue_wait: { en: 'Wait time', he: 'זמן המתנה' },
  queue_channel: { en: 'Channel', he: 'ערוץ' },
  queue_customer: { en: 'Customer', he: 'לקוח' },
  queue_priority: { en: 'Priority', he: 'עדיפות' },
  queue_claim: { en: 'Claim', he: 'קח שיחה' },
  queue_claiming: { en: 'Claiming…', he: 'משייך…' },
  queue_empty: { en: 'Queue is clear', he: 'התור ריק' },
  queue_empty_sub: {
    en: 'No unassigned conversations right now. Nice work.',
    he: 'אין שיחות לא משויכות כרגע. כל הכבוד.',
  },
  queue_load_error: { en: "Couldn't load the queue — retrying.", he: 'לא ניתן לטעון את התור — מנסה שוב.' },
  queue_claim_failed: { en: 'Claim failed — try again', he: 'השיוך נכשל — נסה שוב' },
  queue_reason: { en: 'Reason', he: 'סיבה' },
  urgency_high: { en: 'Urgent', he: 'דחוף' },
  urgency_normal: { en: 'Normal', he: 'רגיל' },
  urgency_low: { en: 'Low', he: 'נמוך' },

  // ── team ──
  team_title: { en: 'Team presence', he: 'נוכחות הצוות' },
  team_sub: { en: '{online} of {total} agents online', he: '{online} מתוך {total} נציגים מחוברים' },
  team_load: { en: 'Active chats', he: 'שיחות פעילות' },
  team_aht: { en: 'Avg. handle time', he: 'זמן טיפול ממוצע' },
  team_resolved: { en: 'Resolved today', he: 'נפתרו היום' },
  team_capacity: { en: 'Capacity', he: 'קיבולת' },
  team_you: { en: 'You', he: 'את/ה' },
  team_empty: { en: 'No agents online right now.', he: 'אין נציגים מחוברים כרגע.' },
  team_load_error: { en: "Couldn't load the team roster.", he: 'לא ניתן לטעון את רשימת הצוות.' },

  // ── settings ──
  set_title: { en: 'User settings', he: 'הגדרות משתמש' },
  set_sub: {
    en: 'Manage your profile, availability and preferences',
    he: 'נהל/י את הפרופיל, הזמינות וההעדפות שלך',
  },
  set_profile: { en: 'Profile', he: 'פרופיל' },
  set_name: { en: 'Full name', he: 'שם מלא' },
  set_email: { en: 'Email', he: 'אימייל' },
  set_phone: { en: 'Phone', he: 'טלפון' },
  set_role: { en: 'Role', he: 'תפקיד' },
  set_change_photo: { en: 'Change photo', he: 'החלפת תמונה' },
  set_avail_cap: { en: 'Availability & capacity', he: 'זמינות וקיבולת' },
  set_default_status: { en: 'My status', he: 'הסטטוס שלי' },
  set_max_chats: { en: 'Max concurrent conversations', he: 'מקסימום שיחות במקביל' },
  set_max_chats_help: {
    en: 'Routing uses this to decide how many conversations to send you.',
    he: 'הניתוב משתמש בזה כדי להחליט כמה שיחות לשלוח אליך.',
  },
  set_autoassign: { en: 'Auto-assign from queue', he: 'שיוך אוטומטי מהתור' },
  set_autoassign_sub: {
    en: 'Receive new conversations automatically when you have capacity',
    he: 'קבל/י שיחות חדשות אוטומטית כשיש מקום פנוי',
  },
  set_notif: { en: 'Notifications', he: 'התראות' },
  set_notif_desktop: { en: 'Desktop notifications', he: 'התראות שולחן עבודה' },
  set_notif_sound: { en: 'Notification sounds', he: 'צלילי התראה' },
  set_notif_new: { en: 'New conversation assigned', he: 'שיחה חדשה שויכה' },
  set_notif_consult: { en: 'Bot requests my help', he: 'הבוט מבקש את עזרתי' },
  set_notif_sla: { en: 'SLA at risk', he: 'SLA בסיכון' },
  set_appearance: { en: 'Appearance', he: 'מראה' },
  set_theme: { en: 'Theme', he: 'ערכת נושא' },
  set_language: { en: 'Language', he: 'שפה' },
  theme_light: { en: 'Light', he: 'בהיר' },
  theme_dark: { en: 'Dark', he: 'כהה' },
  set_quick: { en: 'Quick replies', he: 'תשובות מהירות' },
  set_quick_sub: {
    en: 'Saved responses you can drop into a chat',
    he: 'תגובות שמורות שניתן להוסיף לצ׳אט',
  },
  set_add_reply: { en: 'Add reply', he: 'הוספת תשובה' },
  set_quick_new: { en: 'New saved reply…', he: 'תשובה שמורה חדשה…' },
  set_quick_delete: { en: 'Delete reply', he: 'מחק תשובה' },
  set_security: { en: 'Security', he: 'אבטחה' },
  set_change_pw: { en: 'Change password', he: 'שינוי סיסמה' },
  set_2fa: { en: 'Two-factor authentication', he: 'אימות דו-שלבי' },
  set_2fa_sub: {
    en: 'Add an extra layer of security to your account',
    he: 'הוסף/י שכבת אבטחה נוספת לחשבון',
  },
  set_signout_all: { en: 'Sign out', he: 'התנתקות' },
  set_save: { en: 'Save changes', he: 'שמירת שינויים' },
  set_saved: { en: 'Saved', he: 'נשמר' },
  set_cancel: { en: 'Cancel', he: 'ביטול' },
  set_save_error: { en: 'Save failed — try again', he: 'השמירה נכשלה — נסה שוב' },
  set_soon: { en: 'Coming soon', he: 'בקרוב' },

  // ── WS-5.A — self-serve password reset (ForgotPasswordPage / ResetPasswordPage) ──
  loginForgotPassword: { en: 'Forgot password?', he: 'שכחת סיסמה?' },

  forgotPwTitle: { en: 'Forgot your password?', he: 'שכחת את הסיסמה?' },
  forgotPwSubtitle: {
    en: "Enter your company code and email — we'll send you a reset link if an account exists.",
    he: 'הזן/י את קוד הארגון והאימייל — אם קיים חשבון, נשלח קישור לאיפוס.',
  },
  forgotPwEmail: { en: 'Email', he: 'אימייל' },
  forgotPwSubmit: { en: 'Send reset link', he: 'שלח קישור לאיפוס' },
  forgotPwSubmitting: { en: 'Sending…', he: 'שולח…' },
  forgotPwSuccess: {
    en: 'If an account with that email exists, a reset link is on its way. Check your inbox.',
    he: 'אם קיים חשבון עם האימייל הזה, קישור לאיפוס בדרך. בדוק/י את תיבת הדואר.',
  },
  forgotPwBackToLogin: { en: 'Back to sign in', he: 'חזרה להתחברות' },

  resetPwTitle: { en: 'Set a new password', he: 'הגדרת סיסמה חדשה' },
  resetPwSubtitle: {
    en: 'Choose a new password for your account.',
    he: 'בחר/י סיסמה חדשה לחשבון שלך.',
  },
  resetPwNewPassword: { en: 'New password', he: 'סיסמה חדשה' },
  resetPwConfirmPassword: { en: 'Confirm new password', he: 'אימות סיסמה חדשה' },
  resetPwSubmit: { en: 'Reset password', he: 'אפס סיסמה' },
  resetPwSubmitting: { en: 'Resetting…', he: 'מאפס…' },
  resetPwSuccess: {
    en: 'Your password has been reset. You can now sign in.',
    he: 'הסיסמה שלך אופסה. ניתן להתחבר כעת.',
  },
  resetPwGoToLogin: { en: 'Go to sign in', he: 'עבור להתחברות' },
  resetPwMinLength: {
    en: 'Password must be at least {n} characters.',
    he: 'הסיסמה צריכה להכיל לפחות {n} תווים.',
  },
  resetPwMismatch: { en: 'Passwords do not match.', he: 'הסיסמאות אינן תואמות.' },
  resetPwValidating: { en: 'Checking your link…', he: 'בודק את הקישור…' },
  resetPwInvalidLinkTitle: { en: 'Invalid or expired link', he: 'קישור לא תקין או שפג תוקפו' },
  resetPwInvalidLinkMessage: {
    en: 'This password reset link is invalid or has expired. Request a new one below.',
    he: 'קישור איפוס הסיסמה אינו תקין או שפג תוקפו. בקש/י קישור חדש למטה.',
  },
  resetPwRequestNewLink: { en: 'Request a new link', he: 'בקש קישור חדש' },

  // ── shared non-enumerating / generic error strings (mirror loginErr* family) ──
  resetPwErrNetwork: {
    en: 'Cannot reach the server. Try again later.',
    he: 'לא ניתן להגיע לשרת. נסה שוב מאוחר יותר.',
  },
  resetPwErrInvalidLink: {
    en: 'This link is invalid or has expired. Request a new one.',
    he: 'הקישור אינו תקין או שפג תוקפו. בקש/י קישור חדש.',
  },
  resetPwErrServer: {
    en: 'Server error. Please try again in a moment.',
    he: 'שגיאת שרת. נסה שוב בעוד רגע.',
  },
  resetPwErrGeneric: { en: 'Something went wrong. Please try again.', he: 'משהו השתבש. נסה שוב.' },
  resetPwErrWeakPassword: {
    en: "Your new password doesn't meet the security requirements. Use at least 8 characters, including upper- and lower-case letters, a number, and a symbol.",
    he: 'הסיסמה החדשה אינה עומדת בדרישות האבטחה. יש להשתמש בלפחות 8 תווים, כולל אותיות גדולות וקטנות, ספרה ותו מיוחד.',
  },

  // ── OP-1005 — "Spam / unwanted" on an EMAIL conversation + the "Marked as spam" view ───────────
  // The 24 hours is the server's Sessions.SpamPurge.GraceHours default and is stated in prose; the
  // exact deadline per conversation is shown in the view from the server's purgeAfterUtc.
  spam_btn: { en: 'Spam / unwanted', he: 'ספאם / לא רצוי' },
  spam_dialog_title: {
    en: 'Mark this conversation as spam / unwanted?',
    he: 'לסמן את השיחה כספאם / לא רצוי?',
  },
  spam_dialog_body: {
    en: 'This conversation disappears from the console now and is deleted everywhere after 24 hours; you can press Undo until then. Email drafts that were waiting are cancelled and are not restored.',
    he: 'השיחה תיעלם מהמסוף עכשיו ותימחק מכל המערכת בעוד 24 שעות; אפשר ללחוץ על ביטול הסימון עד אז. טיוטות אימייל שהמתינו יבוטלו ולא ישוחזרו.',
  },
  spam_block_sender: {
    en: 'Also block this sender for this channel',
    he: 'חסום גם את השולח הזה בערוץ הזה',
  },
  spam_confirm: { en: 'Mark as spam', he: 'סמן כספאם' },
  spam_working: { en: 'Marking…', he: 'מסמן…' },
  spam_cancel: { en: 'Cancel', he: 'ביטול' },
  spam_err_generic: {
    en: 'Could not mark this conversation as spam. Nothing was changed.',
    he: 'לא ניתן היה לסמן את השיחה כספאם. שום דבר לא השתנה.',
  },
  nav_spam: { en: 'Marked as spam', he: 'סומנו כספאם' },
  spam_page_title: { en: 'Marked as spam', he: 'סומנו כספאם' },
  spam_page_sub: {
    en: '{n} conversations are waiting to be deleted',
    he: '{n} שיחות ממתינות למחיקה',
  },
  spam_page_sub_one: {
    en: '1 conversation is waiting to be deleted',
    he: 'שיחה אחת ממתינה למחיקה',
  },
  spam_page_note: {
    en: 'These conversations are hidden from every agent and the bot will not answer them. Each is deleted everywhere when its time runs out unless you press Undo. Email drafts that were cancelled when a conversation was marked are not restored by Undo.',
    he: 'השיחות האלה מוסתרות מכל הנציגים והבוט לא יענה להן. כל שיחה נמחקת מכל המערכת כשהזמן שלה נגמר, אלא אם תלחצו על ביטול הסימון. טיוטות אימייל שבוטלו בעת הסימון לא ישוחזרו בביטול הסימון.',
  },
  spam_empty: { en: 'Nothing is marked as spam', he: 'שום שיחה לא סומנה כספאם' },
  spam_empty_sub: {
    en: 'Conversations you mark as spam / unwanted wait here until they are deleted.',
    he: 'שיחות שתסמנו כספאם / לא רצוי ימתינו כאן עד שיימחקו.',
  },
  spam_load_error: {
    en: 'Could not load the list of conversations marked as spam.',
    he: 'לא ניתן היה לטעון את רשימת השיחות שסומנו כספאם.',
  },
  spam_no_access: {
    en: 'You do not have permission to view this page.',
    he: 'אין לך הרשאה לצפות בעמוד הזה.',
  },
  spam_col_what: { en: 'Conversation', he: 'שיחה' },
  spam_col_marked: { en: 'Marked', he: 'סומנה' },
  spam_col_left: { en: 'Time left', he: 'זמן שנותר' },
  spam_col_blocked: { en: 'Sender', he: 'שולח' },
  spam_marked_ago: { en: '{time} ago', he: 'לפני {time}' },
  spam_marked_by: { en: 'by {name}', he: 'על ידי {name}' },
  spam_time_left: { en: 'Deleted in {time}', he: 'תימחק בעוד {time}' },
  spam_due: { en: 'Due for deletion', he: 'מועד המחיקה הגיע' },
  spam_sender_blocked: { en: 'Blocked on this channel', he: 'חסום בערוץ הזה' },
  spam_sender_not_blocked: { en: 'Not blocked', he: 'לא חסום' },
  spam_undo: { en: 'Undo', he: 'ביטול הסימון' },
  spam_undoing: { en: 'Restoring…', he: 'משחזר…' },
  spam_undo_failed: {
    en: 'Could not undo. The conversation is still marked as spam.',
    he: 'לא ניתן היה לבטל את הסימון. השיחה עדיין מסומנת כספאם.',
  },

  // ── Console v2 foundation labels (C1-5) ───────────────────────────────────────────────────────────────────
  // Most are consumed by PR-C2 (folder rail, list, tags, ownership) and C1-6 (contact panel); they land here so
  // the foundation PR carries the full bilingual set and one test (labels.acv2.test.ts) pins it.
  // he COPIED from the approved prototype's i18n.ts / state/folders.ts where the concept exists: fr_title,
  // fr_sec_*, fr_collapse/expand, fr_no_tags, f_* labels and rules, cl_loading_more, cl_empty, cl_all_loaded,
  // tags_add, search_results, cp_title, cp_past_conversations, role_*, btn_return_team(+_hint).
  // ASSEMBLED from prototype pieces, not copied verbatim (NEED NATIVE REVIEW): cl_showing (the prototype has the
  // words 'Showing' and 'of' separately), and the tle_returned_to_team Hebrew, which follows the prototype's
  // sysReturned wording.
  // HE NEEDS NATIVE REVIEW (new Hebrew written for this PR): fr_restricted, fr_no_folders, cl_load_failed,
  // cl_retry, row_private_to, row_tags_more, tags_search_ph, tags_none_catalog, tags_remove, tags_save_failed,
  // cp_continues, cp_continued_in, cp_hide, cp_show, tle_returned_by_admin, tle_tags_changed, btn_return_team_admin,
  // return_team_confirm, conv_withdrawn.

  // Roles and ownership buttons
  role_senior_agent: { en: 'Senior agent', he: 'נציג בכיר' },
  role_admin: { en: 'Admin', he: 'מנהל' },
  btn_return_team: { en: 'Return to team', he: 'החזרה לצוות' },
  btn_return_team_hint: {
    en: 'Give up ownership — it goes back to Team shared',
    he: 'ויתור על הבעלות — השיחה חוזרת למשותפות הצוות',
  },
  btn_return_team_admin: { en: 'Return to team (admin)', he: 'החזרה לצוות (מנהל)' },
  return_team_confirm: {
    en: '{name} owns this conversation. Return it to the team?',
    he: 'השיחה בבעלות {name}. להחזיר אותה לצוות?',
  },
  return_team_confirm_mine: {
    en: 'Return this conversation to the team? It becomes visible to the whole team and you stop owning it.',
    he: 'להחזיר את השיחה לצוות? היא תהפוך לגלויה לכל הצוות ולא תהיה עוד בבעלותך.',
  },
  return_team_confirm_admin: {
    en: '{name} owns this conversation. Return it to the team? It becomes visible to the whole team.',
    he: 'השיחה בבעלות {name}. להחזיר אותה לצוות? היא תהפוך לגלויה לכל הצוות.',
  },
  return_team_cancel: { en: 'Cancel', he: 'ביטול' },
  return_team_working: { en: 'Returning…', he: 'מחזיר…' },
  return_team_failed: {
    en: 'Could not return the conversation to the team. It was not changed.',
    he: 'לא ניתן היה להחזיר את השיחה לצוות. השיחה לא השתנתה.',
  },
  conv_withdrawn: {
    en: 'This conversation is no longer visible to you — someone else took it.',
    he: 'השיחה כבר אינה גלויה לך — מישהו אחר לקח אותה.',
  },

  // Folder rail
  fr_title: { en: 'Help Desk', he: 'מוקד תמיכה' },
  fr_sec_my_work: { en: 'My work', he: 'העבודה שלי' },
  // PR-A (2026-10-26): the Team folders are team-wide BY STATE (owned conversations included), so the heading is "Team".
  fr_sec_team_shared: { en: 'Team', he: 'צוות' },
  // The rail section of the tenant's own conversation statuses (ADR-017 state defs, `cat:<key>` folders).
  fr_sec_tags: { en: 'Tags', he: 'תגיות' },
  fr_sec_more: { en: 'More', he: 'עוד' },
  fr_collapse: { en: 'Collapse folders', he: 'כיווץ תיקיות' },
  fr_expand: { en: 'Expand folders', he: 'הרחבת תיקיות' },
  fr_restricted: { en: 'Visible to senior agents and admins only', he: 'גלוי לנציגים בכירים ולמנהלים בלבד' },
  fr_no_tags: { en: 'No tags yet', he: 'אין עדיין תגיות' },

  // System folders: label + the rule behind it (shown in the tooltip). Keep the rules identical to the server's
  // folder definitions.
  f_mine: { en: 'My conversations', he: 'השיחות שלי' },
  f_mine_rule: {
    en: 'Everything you own: your consultations, your takeovers and what you released to the bot.',
    he: 'כל מה שבבעלותך: ההתייעצויות, ההשתלטויות ומה ששחררת לבוט.',
  },
  f_my_consult: { en: 'My consultations', he: 'ההתייעצויות שלי' },
  f_my_consult_rule: {
    en: 'You answered the bot; the bot keeps talking to the customer with your guidance.',
    he: 'ענית לבוט; הבוט ממשיך מול הלקוח לפי ההנחיה שלך.',
  },
  f_my_takeover: { en: 'Takeover', he: 'השתלטות' },
  f_my_takeover_rule: { en: 'You took over and write to the customer yourself.', he: 'השתלטת וכותב/ת ללקוח בעצמך.' },
  f_my_released: { en: 'Released to bot', he: 'שוחררו לבוט' },
  f_my_released_rule: {
    en: 'You handed it back to the bot but it is still yours.',
    he: 'החזרת לבוט אבל השיחה עדיין שלך.',
  },
  // Team folders: "All open" is the one fixed team folder; the tenant's conversation statuses (the closed one included)
  // follow it, labelled by the tenant. Which rows you can see is the server's rule (private conversations on: yours and
  // unowned ones; senior agents and admins see all).
  f_all_open: { en: 'All Open Tickets', he: 'כל הפניות הפתוחות' },
  f_all_open_rule: { en: 'Every open conversation you can see.', he: 'כל השיחות הפתוחות שגלויות לך.' },
  f_drafts: { en: 'Drafts', he: 'טיוטות' },
  f_drafts_rule: { en: 'A reply draft is waiting for review.', he: 'טיוטת תשובה ממתינה לבדיקה.' },

  // Conversation-status overhaul (2026-11-02). HE NEEDS NATIVE REVIEW for the sentences; the short labels are the operator's.
  // List lifecycle tabs
  cl_tabs_label: { en: 'Show conversations', he: 'הצגת שיחות' },
  cl_tab_all: { en: 'All', he: 'הכל' },
  cl_tab_need_help: { en: 'Need help', he: 'דרושה עזרה' },
  cl_tab_bot: { en: 'Bot handling', he: 'הבוט מטפל' },
  cl_tab_agent: { en: 'Agent handling', he: 'נציג מטפל' },
  // Row line 2: who wrote the newest message (only a bot or agent message is labelled)
  row_prev_bot: { en: 'Bot', he: 'בוט' },
  row_prev_agent: { en: 'Agent', he: 'נציג' },
  row_prev_agent_named: { en: 'Agent ({name})', he: 'נציג ({name})' },
  // Priority
  conv_priority: { en: 'Priority', he: 'עדיפות' },
  priority_low: { en: 'Low', he: 'נמוכה' },
  priority_medium: { en: 'Medium', he: 'בינונית' },
  priority_high: { en: 'High', he: 'גבוהה' },
  priority_urgent: { en: 'Urgent', he: 'דחופה' },
  conv_priority_save_failed: { en: 'Could not save the priority. Try again.', he: 'לא ניתן היה לשמור את העדיפות. נסו שוב.' },
  conv_priority_pin_hint: {
    en: 'Setting it by hand pins it: the system stops changing it.',
    he: 'קביעה ידנית מקבעת אותה: המערכת מפסיקה לשנות אותה.',
  },
  // Status picker refusals when a closed conversation is moved to another status (a reopen)
  conv_category_reopen_window_passed: {
    en: 'This conversation was closed too long ago to be reopened.',
    he: 'השיחה נסגרה לפני זמן רב מדי ואי אפשר לפתוח אותה מחדש.',
  },
  conv_category_reopen_disabled: {
    en: 'Reopening closed conversations is switched off.',
    he: 'פתיחה מחדש של שיחות סגורות כבויה.',
  },

  // Conversation list
  cl_loading_more: { en: 'Loading more conversations…', he: 'טוען שיחות נוספות…' },
  cl_showing: { en: 'Showing {n} of {total}', he: 'מוצגות {n} מתוך {total}' },
  cl_all_loaded: { en: 'All conversations loaded · {n}', he: 'כל השיחות נטענו · {n}' },
  cl_empty: { en: 'Nothing in this folder right now.', he: 'אין כרגע שיחות בתיקייה הזו.' },
  cl_load_failed: { en: 'Could not load conversations.', he: 'לא ניתן היה לטעון שיחות.' },
  cl_retry: { en: 'Try again', he: 'נסו שוב' },
  row_private_to: { en: 'Private to {name}', he: 'פרטי ל{name}' },
  row_tags_more: { en: '+{n} more', he: 'ועוד {n}' },

  // Per-folder empty states and the loaded-rows filter (console v2, C2-6). HE NEEDS NATIVE REVIEW (all new Hebrew).
  cl_empty_mine: {
    en: 'You do not own any conversation yet. Answer the bot or take one over and it appears here.',
    he: 'עדיין אין שיחות בבעלותך. כשתענה לבוט או תשתלט על שיחה — היא תופיע כאן.',
  },
  cl_empty_my_consult: { en: 'No consultations of yours right now.', he: 'אין כרגע התייעצויות שלך.' },
  cl_empty_my_takeover: { en: 'You have not taken over any conversation.', he: 'לא השתלטת על אף שיחה.' },
  cl_empty_my_released: { en: 'Nothing you released to the bot.', he: 'אין שיחות ששחררת לבוט.' },
  cl_empty_all_open: { en: 'No open conversations right now.', he: 'אין כרגע שיחות פתוחות.' },
  cl_empty_drafts: { en: 'No reply drafts are waiting for review.', he: 'אין טיוטות תשובה הממתינות לבדיקה.' },
  cl_empty_all_closed: { en: 'No closed conversations yet.', he: 'אין עדיין שיחות סגורות.' },
  // PR-C3 (C3-1) server-side search. HE NEEDS NATIVE REVIEW (all new Hebrew): cl_search_note, cl_search_clear, cl_empty_search.
  // (The C2-6 loaded-rows filter - cl_no_match, cl_filter_note, cl_filter_clear - is replaced by the server search.)
  cl_search_note: {
    en: 'Results for "{q}" among every conversation you can see, open and closed.',
    he: 'תוצאות עבור "{q}" מכל השיחות שמותר לך לראות, פתוחות וסגורות.',
  },
  cl_search_clear: { en: 'Clear search', he: 'ניקוי החיפוש' },
  cl_empty_search: { en: 'No conversation matches "{q}".', he: 'אין שיחה שתואמת ל-"{q}".' },

  // Tags
  tags_add: { en: 'Add tag', he: 'הוספת תגית' },
  tags_search_ph: { en: 'Find a tag…', he: 'חיפוש תגית…' },
  tags_none_catalog: {
    en: 'No tags are defined for this workspace yet.',
    he: 'עדיין לא הוגדרו תגיות בסביבת העבודה.',
  },
  tags_remove: { en: 'Remove tag {label}', he: 'הסרת התגית {label}' },
  tags_save_failed: { en: 'Could not save the tags. Try again.', he: 'לא ניתן היה לשמור את התגיות. נסו שוב.' },
  // C2-7 (HE NEEDS NATIVE REVIEW): tags_no_match, tags_admin_hint, return_team_confirm_mine, return_team_confirm_admin,
  // return_team_failed, return_team_cancel, return_team_working.
  tags_no_match: { en: 'No tag matches.', he: 'אין תגית תואמת.' },
  tags_admin_hint: {
    en: 'Tags are managed by your admin in the Admin Portal.',
    he: 'התגיות מנוהלות על ידי מנהל המערכת בפורטל הניהול.',
  },

  // Search and contact panel
  search_results: { en: 'Search results', he: 'תוצאות חיפוש' },
  cp_title: { en: 'Contact', he: 'איש קשר' },
  cp_hide: { en: 'Hide contact info', he: 'הסתרת פרטי איש הקשר' },
  cp_show: { en: 'Show contact info', he: 'הצגת פרטי איש הקשר' },
  cp_past_conversations: { en: 'Past conversations', he: 'שיחות קודמות' },
  cp_continues: { en: 'Continues an earlier conversation', he: 'המשך של שיחה קודמת' },
  cp_continued_in: { en: 'Continued in a later conversation', he: 'נמשכה בשיחה מאוחרת יותר' },
  // PR-C3 (C3-2) past conversations in the contact panel. HE NEEDS NATIVE REVIEW (all new Hebrew): cp_past_empty,
  // cp_past_failed, cp_past_loading (plus cp_continues / cp_continued_in above, first used here).
  cp_past_empty: { en: 'No earlier conversations', he: 'אין שיחות קודמות' },
  cp_past_failed: { en: 'Could not load earlier conversations.', he: 'לא ניתן היה לטעון שיחות קודמות.' },
  cp_past_loading: { en: 'Loading earlier conversations…', he: 'טוען שיחות קודמות…' },

  // Timeline entry types added by the ownership / tags work
  tle_returned_to_team: { en: '{name} returned it to the team', he: '{name} החזיר/ה את השיחה למשותפות הצוות' },
  tle_returned_by_admin: { en: '{name} (admin) returned it to the team', he: '{name} (מנהל) החזיר/ה את השיחה לצוות' },
  tle_tags_changed: { en: '{name} changed the tags', he: '{name} שינה/תה את התגיות' },

  // ── Contact panel (C1-6) ──────────────────────────────────────────────────────────────────────────────────────
  // cp_owner_team he COPIED from the prototype's i18n.ts ('teamShared' = fr_sec_team_shared). HE NEEDS NATIVE REVIEW
  // (new Hebrew written for this PR): cp_contact_details.
  cp_owner_team: { en: 'Team shared', he: 'משותף לצוות' },
  cp_contact_details: { en: 'Contact details', he: 'פרטי איש קשר' },

  // ── 2026-10-28 (operator) — "the customer asked for a person" + the bot's proposed replies ─────────────────────────
  // human_banner and human_badge are the operator's own wording. The rest is new Hebrew, written gender-neutral (plural
  // imperative / noun forms) and NEEDS NATIVE REVIEW before release.
  human_banner: { en: 'The customer asked to talk to a person', he: 'הלקוח ביקש לדבר עם נציג' },
  act_human_requested: { en: '{name} asked to talk to a person', he: 'בקשה לדבר עם נציג מ-{name}' },
  pr_head: { en: 'Suggested replies', he: 'תשובות מוצעות' },
  pr_group: { en: 'Replies the bot suggests for the customer', he: 'תשובות שהבוט מציע ללקוח' },
  pr_send: { en: 'Send to customer', he: 'שליחה ללקוח' },
  pr_send_aria: { en: 'Send suggested reply {n} to the customer', he: 'שליחת תשובה מוצעת {n} ללקוח' },
  pr_sending: { en: 'Sending…', he: 'בשליחה…' },
  pr_sent: { en: 'Sent', he: 'נשלחה' },
  pr_edit_aria: { en: 'Edit suggested reply {n} before sending', he: 'עריכת תשובה מוצעת {n} לפני שליחה' },
  pr_failed: { en: 'The reply was not sent. Try again.', he: 'התשובה לא נשלחה. נסו שוב.' },
} satisfies Record<string, Entry>;

export type LabelKey = keyof typeof LABELS;

/** Values interpolated into `{token}` placeholders. */
export type LabelVars = Record<string, string | number>;

/** Replace `{name}` style tokens in a resolved string. No-op when vars omitted. */
export function interpolate(s: string, vars?: LabelVars): string {
  if (!vars) return s;
  let out = s;
  for (const k of Object.keys(vars)) {
    out = out.replace(new RegExp('\\{' + k + '\\}', 'g'), () => String(vars[k])); // function replacer: a value such as '$&' or "$'" (a search text from the URL) is inserted literally, never as a replacement pattern
  }
  return out;
}

export function translate(key: LabelKey, lang: Lang, vars?: LabelVars): string {
  return interpolate(LABELS[key][lang], vars);
}

// ─── Enum mappers (backend value → friendly bilingual label) ─────────
// Each takes the raw backend string and falls back to the raw value if it's
// an unknown variant, so a new server enum never renders blank.

const STATE: Record<string, Entry> = {
  AI_HANDLING: { en: 'Bot', he: 'בוט' },
  CONSULTATION_REQUESTED: { en: 'Needs help', he: 'דרושה עזרה' },
  HUMAN_TAKEOVER: { en: 'With agent', he: 'אצל נציג' },
  CLOSED: { en: 'Closed', he: 'סגור' },
};

const URGENCY: Record<string, Entry> = {
  CRITICAL: { en: 'Urgent', he: 'דחוף' },
  WARNING: { en: 'Due soon', he: 'בקרוב' },
  NORMAL: { en: 'On track', he: 'תקין' },
};

const SENDER: Record<string, Entry> = {
  CUSTOMER: { en: 'Customer', he: 'לקוח' },
  AI: { en: 'Bot', he: 'בוט' },
  AGENT: { en: 'Agent', he: 'נציג' },
};

const AVAILABILITY: Record<string, Entry> = {
  ONLINE: { en: 'Available', he: 'זמין' },
  BUSY: { en: 'Busy', he: 'עסוק' },
  OFFLINE: { en: 'Offline', he: 'לא מחובר' },
};

const HUB_STATUS: Record<string, Entry> = {
  connected: { en: 'Connected', he: 'מחובר' },
  connecting: { en: 'Connecting…', he: 'מתחבר…' },
  reconnecting: { en: 'Reconnecting…', he: 'מתחבר מחדש…' },
  disconnected: { en: 'Offline', he: 'לא מחובר' },
};

const CHANNEL: Record<string, Entry> = {
  webchat: { en: 'Web chat', he: 'צ׳אט באתר' },
  whatsapp: { en: 'WhatsApp', he: 'וואטסאפ' },
  sms: { en: 'SMS', he: 'SMS' },
  email: { en: 'Email', he: 'אימייל' },
  messenger: { en: 'Messenger', he: 'מסנג׳ר' },
};

function pick(map: Record<string, Entry>, raw: string, lang: Lang): string {
  return map[raw]?.[lang] ?? raw;
}

export const stateLabel = (raw: string, lang: Lang) => pick(STATE, raw, lang);
export const urgencyLabel = (raw: string, lang: Lang) => pick(URGENCY, raw, lang);
export const senderLabel = (raw: string, lang: Lang) => pick(SENDER, raw, lang);
export const availabilityLabel = (raw: string, lang: Lang) => pick(AVAILABILITY, raw, lang);
export const hubStatusLabel = (raw: string, lang: Lang) => pick(HUB_STATUS, raw, lang);
export const channelLabel = (raw: string, lang: Lang) =>
  pick(CHANNEL, (raw ?? '').toLowerCase(), lang);

// ─── React hook ──────────────────────────────────────────────────────

/**
 * Subscribe a component to the current language and get bound translators.
 * Re-renders when the agent flips the language toggle.
 */
export function useT() {
  const lang = useLangStore((s) => s.lang);
  return {
    lang,
    t: (key: LabelKey, vars?: LabelVars) => translate(key, lang, vars),
    stateLabel: (raw: string) => stateLabel(raw, lang),
    urgencyLabel: (raw: string) => urgencyLabel(raw, lang),
    senderLabel: (raw: string) => senderLabel(raw, lang),
    availabilityLabel: (raw: string) => availabilityLabel(raw, lang),
    hubStatusLabel: (raw: string) => hubStatusLabel(raw, lang),
    channelLabel: (raw: string) => channelLabel(raw, lang),
  };
}
